/**
 * Campaign Preview Component
 * 
 * Live preview of campaign details as user fills the form:
 * - Campaign name and type
 * - Validity dates
 * - Linked promos count
 * - Funding information
 * - Updates in real-time based on form data
 */

import React from 'react';
import { CampaignFormData, CampaignType } from '../../../../../api/promos/types';
import './CampaignPreview.css';

interface CampaignPreviewProps {
  formData: CampaignFormData;
  currentStep: number;
}

const CampaignPreview: React.FC<CampaignPreviewProps> = ({
  formData,
  currentStep,
}) => {
  // Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString || dateString.trim() === '') return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  // Format campaign type for display
  const formatCampaignType = (type: CampaignType): string => {
    return type.charAt(0) + type.slice(1).toLowerCase();
  };

  return (
    <div className="campaign-preview-card">
      <h6 className="campaign-preview-header">Preview</h6>
      
      {/* Campaign Name */}
      <div className="campaign-preview-item">
        <span className="campaign-preview-label">Campaign Name</span>
        <div className="campaign-preview-content multi-line">
          {formData.name || '-'}
        </div>
      </div>

      {/* Campaign Type */}
      <div className="campaign-preview-item">
        <span className="campaign-preview-label">Campaign Type</span>
        <div className="campaign-preview-content">
          {formData.campaignType ? formatCampaignType(formData.campaignType) : '-'}
        </div>
      </div>

      {/* Validity */}
      <div className="campaign-preview-item">
        <span className="campaign-preview-label">Validity</span>
        <div className="campaign-preview-content multi-line">
          {formData.startAt && formData.endAt 
            ? `${formatDate(formData.startAt)} — ${formatDate(formData.endAt)}`
            : '-'}
        </div>
      </div>

      {/* Description */}
      {formData.description && (
        <div className="campaign-preview-item">
          <span className="campaign-preview-label">Description</span>
          <div className="campaign-preview-content multi-line">
            {formData.description}
          </div>
        </div>
      )}

      {/* Show additional details based on current step */}
      {currentStep >= 1 && formData.linkedPromos && formData.linkedPromos.length > 0 && (
        <>
          {/* Linked Promos */}
          <div className="campaign-preview-item">
            <span className="campaign-preview-label">Linked Promos</span>
            <div className="campaign-preview-content">
              {formData.linkedPromos.length}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CampaignPreview;